import {
  FlightPurchaseGuestDetailModel,
  FlightPurchaseModel,
} from 'api-hooks/order/ticket-order';
import {
  FlightSaleGuestDetailModel,
  TicketSaleModel,
} from 'api-hooks/sale/ticket-sale';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { distinct } from 'common/utils/iterable';
import { generateId } from 'common/utils/string';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  TicketOrderFlightPurchaseAddOnsFormType,
  TicketOrderFlightPurchaseAirlineFormType,
  TicketOrderFlightPurchaseFormType,
  TicketOrderFlightPurchaseGuestFormType,
  TicketOrderFormType,
} from '../components/form-type';

interface Props {
  flightPurchase?: FlightPurchaseModel;
  flightSale?: TicketSaleModel;
  defaultValues?: TicketOrderFormType;
}

export function addNewAddOns(): TicketOrderFlightPurchaseAddOnsFormType {
  return {
    customId: generateId(),
    description: '',
    guestId: '',
    route: '',
    guestName: '',
    id: undefined,
    price: undefined,
  } as never as TicketOrderFlightPurchaseAddOnsFormType;
}

export function addNewAirline(): TicketOrderFlightPurchaseAirlineFormType {
  return {
    airlineId: '',
    airlineName: '',
    customId: generateId(),
    detail: '',
    preferenceId: '',
    preferenceName: '',
    route: '',
    time: '',
    date: undefined,
    price: undefined,
  } as never as TicketOrderFlightPurchaseAirlineFormType;
}

export function addNewGuest(): TicketOrderFlightPurchaseGuestFormType {
  return {
    guestId: '',
    guestName: '',
    ticket: '',
    customId: generateId(),
  } as never as TicketOrderFlightPurchaseGuestFormType;
}

export function addNewFlightPurchase(): TicketOrderFlightPurchaseFormType {
  return {
    bookingCode: '',
    flightPurchaseAddOns: [],
    flightPurchaseAirlines: [addNewAirline()],
    flightPurchaseGuests: [addNewGuest()],
    customId: generateId(),
  } as never as TicketOrderFlightPurchaseFormType;
}

export function generateUniqueAirlineItem(
  fpg: FlightPurchaseGuestDetailModel | FlightSaleGuestDetailModel,
) {
  return (
    fpg.airline.id +
    fpg.preference?.id +
    fpg.date +
    fpg.price +
    fpg.detail +
    fpg.route +
    fpg.time
  );
}

export function tourListToFlightPurchase(
  tourList: TourModel,
): TicketOrderFormType {
  const transactionAt = new Date();
  const defaultValues: TicketOrderFormType = {
    orderNumber: '',
    saleTotal: 0,
    agentId: '',
    agentStatus: '',
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    code: '',
    commission: 0,
    description: '',
    discount: 0,
    invoiceId: '',
    invoiceRefId: '',
    needApproval: false,
    otherCost: 0,
    paymentChannelId: '',
    refId: undefined,
    parentRefId: '',
    tempTransactionAt: transactionAt,
    transactionAt,
    tourId: tourList.id,
    tourRefId: tourList.refId,
    type: 'tour',
    flightSale: undefined,
    flightPurchase: undefined,
    total: 0,
    flightPurchases: [addNewFlightPurchase()], //flight purchases will be fill with select sales
  };
  return defaultValues;
}

export default function useGetDefaultValues(props: Props) {
  const { flightPurchase, flightSale } = props;
  const defaultValues = React.useMemo<TicketOrderFormType>(() => {
    const transactionAt = flightPurchase?.transactionAt || new Date();
    if (props.defaultValues) return props.defaultValues;
    if (flightSale) {
      return {
        orderNumber: '',
        saleTotal: flightSale.total,
        agentId: '',
        agentStatus: '',
        remainingTransactionQuota: null,
        isExceedingQuota: false,
        code: flightSale.code || '',
        commission: 0,
        description: '',
        discount: flightSale.discount || 0,
        invoiceId: flightSale.id,
        invoiceRefId: flightSale.refId,
        needApproval: false,
        otherCost: flightSale.otherCost || 0,
        paymentChannelId: '',
        refId: undefined,
        parentRefId: '',
        tempTransactionAt: transactionAt,
        transactionAt,
        tourId: undefined,
        tourRefId: undefined,
        type: 'sales',
        flightSale,
        flightPurchase,
        total: 0,
        flightPurchases: [addNewFlightPurchase()], //flight purchases will be fill with select sales
      };
    }
    const agent = flightPurchase?.agent;
    const paymentChannel = flightPurchase?.paymentChannel;
    const sale = flightPurchase?.sale;
    const tour = flightPurchase?.tour;

    const flightPurchases: TicketOrderFormType['flightPurchases'] =
      flightPurchase?.flightPurchases?.map((fp) => {
        const flightPurchaseAddOns: TicketOrderFlightPurchaseAddOnsFormType[] =
          fp.flightPurchaseAddOns.map((fpao) => {
            const guest = fpao.guest;
            return {
              customId: generateId(),
              description: fpao.description,
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: fpao.id,
              price: fpao.price,
              route: fpao.route,
            };
          });

        const flightPurchaseAirlines: TicketOrderFlightPurchaseAirlineFormType[] =
          distinct(fp.flightPurchaseGuests, (guest) =>
            generateUniqueAirlineItem(guest),
          ).map((fgp) => {
            const airline = fgp.airline;
            const preference = fgp.preference;
            return {
              airlineId: airline.id,
              airlineName: airline.name,
              customId: generateId(),
              preferenceId: preference?.id || '',
              preferenceName: preference?.name || '',
              date: fgp.date,
              detail: fgp.detail || '',
              price: fgp.price || 0,
              route: fgp.route || '',
              time: fgp.time || '',
            };
          });

        const flightPurchaseGuests: TicketOrderFlightPurchaseGuestFormType[] =
          distinct(fp.flightPurchaseGuests, (guest) => guest.guest.id).map(
            (fgp) => {
              const guest = fgp.guest;
              return {
                customId: generateId(),
                guestId: guest.id,
                guestName: guestNameTransformer(guest),
                ticket: fgp.ticket || '',
              };
            },
          );
        return {
          bookingCode: fp.bookingCode || '',
          customId: generateId(),
          flightPurchaseAddOns,
          flightPurchaseAirlines,
          flightPurchaseGuests,
        };
      }) ?? [addNewFlightPurchase()];

    return {
      agentId: agent?.id || '',
      agentStatus: agent?.status || '',
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      paymentChannelId: paymentChannel?.id || '',
      code: flightPurchase?.code || '',
      orderNumber: flightPurchase?.number || '',
      commission: flightPurchase?.agentCommission?.amount || 0,
      description: flightPurchase?.description || '',
      discount: flightPurchase?.discount || 0,
      refId: flightPurchase?.refId,
      parentRefId: flightPurchase?.parentPurchase?.id,
      invoiceId: sale?.id,
      tourId: tour?.id,
      tourRefId: flightPurchase?.tour?.refId,
      invoiceRefId: flightPurchase?.invoiceRefId,
      needApproval: false,
      transactionAt,
      tempTransactionAt: transactionAt,
      otherCost: flightPurchase?.otherCost || 0,
      total: flightPurchase?.total || 0,
      saleTotal: sale?.total || 0,
      type: flightPurchase?.type || 'sales',
      flightPurchases,

      flightPurchase,
      flightSale,
    };
  }, [flightPurchase, flightSale, props.defaultValues]);

  return defaultValues;
}
